import { tables, emptySnapshot, type Snapshot, type RecordStorage } from '@ventureroom/core';
import type { Preferences } from '@ventureroom/ui';
function open() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open('ventureroom', 1);
    req.onupgradeneeded = () => {
      for (const table of tables) req.result.createObjectStore(table, { keyPath: 'id' });
      req.result.createObjectStore('meta');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
export const storage: RecordStorage = {
  async load() {
    const db = await open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([...tables, 'meta'], 'readonly');
      const result = emptySnapshot();
      let seeded = false;
      const marker = tx.objectStore('meta').get('seeded');
      marker.onsuccess = () => {
        seeded = !!marker.result;
      };
      for (const table of tables) {
        const req = tx.objectStore(table).getAll();
        req.onsuccess = () => Object.assign(result, { [table]: req.result });
      }
      tx.oncomplete = () => {
        db.close();
        resolve(seeded ? result : null);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  },
  async save(s: Snapshot) {
    const db = await open();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction([...tables, 'meta'], 'readwrite');
      for (const table of tables) {
        const store = tx.objectStore(table);
        store.clear();
        for (const record of s[table]) store.put(record);
      }
      tx.objectStore('meta').put(true, 'seeded');
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
      tx.onabort = () => {
        db.close();
        reject(tx.error ?? Error('Save cancelled.'));
      };
    });
  },
};
export const preferences: Preferences = {
  async get(key) {
    return (key === 'backend-token' ? sessionStorage : localStorage).getItem(`vr:${key}`);
  },
  async set(key, value) {
    (key === 'backend-token' ? sessionStorage : localStorage).setItem(`vr:${key}`, value);
  },
};

function openDb(name: string) {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(name, 1);
    req.onupgradeneeded = () => {
      for (const table of tables) req.result.createObjectStore(table, { keyPath: 'id' });
      req.result.createObjectStore('meta');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function storageForUser(userId: string): RecordStorage {
  const dbName = `ventureroom-${userId}`;
  return {
    async load() {
      const db = await openDb(dbName);
      return new Promise((resolve, reject) => {
        const tx = db.transaction([...tables, 'meta'], 'readonly');
        const result = emptySnapshot();
        let seeded = false;
        const marker = tx.objectStore('meta').get('seeded');
        marker.onsuccess = () => { seeded = !!marker.result; };
        for (const table of tables) {
          const req = tx.objectStore(table).getAll();
          req.onsuccess = () => Object.assign(result, { [table]: req.result });
        }
        tx.oncomplete = () => { db.close(); resolve(seeded ? result : null); };
        tx.onerror = () => { db.close(); reject(tx.error); };
      });
    },
    async save(s: Snapshot) {
      const db = await openDb(dbName);
      return new Promise<void>((resolve, reject) => {
        const tx = db.transaction([...tables, 'meta'], 'readwrite');
        for (const table of tables) {
          const store = tx.objectStore(table);
          store.clear();
          for (const record of s[table]) store.put(record);
        }
        tx.objectStore('meta').put(true, 'seeded');
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
        tx.onabort = () => { db.close(); reject(tx.error ?? Error('Save cancelled.')); };
      });
    },
  };
}
